// ---------------------------------------------------------------------------
// HOTEL DESK - NAVIGATION ARCHITECTURE  (single source of truth)
//
// RULES ENCODED HERE:
//   1. The sidebar has ONE level only - these MODULES. Never a nested sidebar.
//   2. Second level is ALWAYS horizontal tabs (a "view" you look at).
//   3. TASKS (New Booking, Walk In...) are NOT tabs - they are `actions`
//      that open in a drawer over the current view, so staff never lose place.
//   4. Every tab carries its EXISTING menuKey + EXISTING route, so
//      permissions (UserMenuPermission), plans (PlanMenus) and add-ons
//      (AddonMenus) keep working with ZERO backend change.
//
// VISIBILITY RULE (same as today's sidebar):
//   tab visible  = keys.has(tab.menuKey) && planAllowed(tab.menuKey)
//   module visible = it has >= 1 visible tab
// ---------------------------------------------------------------------------

export const MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'Dashboard',
    tabs: [
      { key: 'overview',   label: 'Overview',   menuKey: 'dashboard',          path: '/admin/dashboard' },
      { key: 'restaurant', label: 'Restaurant', menuKey: 'resturantdashbord',  path: '/admin/resturantdashbord' },
    ],
  },

  {
    key: 'frontdesk',
    label: 'Front Desk',
    icon: 'Hotel',
    subtitle: 'Manage reservations and guest stays.',
    // TASKS -> drawers. NOT tabs. (CPO decision: keeps staff on their list.)
    // `hidden: true` keeps the path→module mapping (so these pages still get the
    // Front Desk chrome) but removes the buttons from the header top-right — the
    // sidebar already has New Booking / Quick Booking, so they were duplicates.
    actions: [
      { key: 'newBooking',   label: 'New Booking',   menuKey: 'manageinventory', path: '/admin/manageinventory', primary: true, hidden: true },
      { key: 'quickBooking', label: 'Quick Booking', menuKey: 'QuickBooking',    path: '/admin/QuickBooking', hidden: true },
      { key: 'hourly',       label: 'Hourly',        menuKey: 'HourlyBooking',   path: '/admin/HourlyBooking', hidden: true },
    ],
    // Reception pages as a connected tab flow (left → right). Their routes live
    // inside the Front Desk ModuleLayout wrapper (App.js) so the strip stays on
    // every one. menuKeys mirror the sidebar's so permissions match.
    tabs: [
      { key: 'today',        label: 'Today',            menuKey: 'BookingReport',   path: '/admin/ReceptionToday' },
      { key: 'newbooking',   label: 'New Booking',      menuKey: 'manageinventory', path: '/admin/manageinventory' },
      { key: 'roomresv',     label: 'Room Reservation', menuKey: 'QuickBooking',    path: '/admin/RoomReservation' },
      { key: 'arrivals',     label: 'Arrivals',         menuKey: 'BookingReport',   path: '/admin/Arrivals' },
      { key: 'departures',   label: 'Departures',       menuKey: 'BookingReport',   path: '/admin/Departures' },
      { key: 'ota',          label: 'OTA Bookings',     menuKey: 'BookingReport',   path: '/admin/OtaBookings' },
      { key: 'rooms',        label: 'Rooms',            menuKey: 'BookingReport',   path: '/admin/RoomGrid' },
      { key: 'bookings',     label: 'Bookings',         menuKey: 'BookingReport',   path: '/admin/BookingReport' },
      { key: 'roomcal',      label: 'Room Calendar',    menuKey: 'Calender',        path: '/admin/Calender' },
      { key: 'changeroom',   label: 'Change Room',      menuKey: 'Changeroom',      path: '/admin/Changeroom' },
    ],
  },

  {
    key: 'restaurant',
    label: 'Restaurant',
    icon: 'Restaurant',
    subtitle: 'Orders, tables and kitchen.',
    actions: [
      { key: 'createOrder', label: 'New Order', menuKey: 'CreateOrder', path: '/admin/CreateOrder', primary: true },
      { key: 'quickBill',   label: 'Quick Bill', menuKey: 'CreateOrder', path: '/admin/QuickBill' },
    ],
    tabs: [
      { key: 'tables',   label: 'Table Orders',   menuKey: 'RestaurantOrder',      path: '/admin/TableOrders' },
      { key: 'rooms',    label: 'Room Orders',    menuKey: 'Order',                path: '/admin/Order' },
      { key: 'kitchen',  label: 'Kitchen',        menuKey: 'KitchenPendingOrders', path: '/admin/KitchenPendingOrders' },
      { key: 'kot',      label: 'KOT',            menuKey: 'KotReport',            path: '/admin/KotReport' },
      { key: 'payments', label: 'Payments',       menuKey: 'RestaurantOrder',      path: '/admin/RestaurantPayments' },
      { key: 'invoices', label: 'Bills',          menuKey: 'RestaurantInvoiceReport', path: '/admin/RestaurantInvoiceReport' },
    ],
  },

  {
    key: 'inventory',
    label: 'Inventory & Purchase',
    icon: 'Inventory',
    subtitle: 'Stock, suppliers and purchasing.',
    // All Store sidebar pages as a connected tab flow (order mirrors the sidebar
    // workflow). Their routes live inside the inventory ModuleLayout wrapper
    // (App.js) so the strip stays on every one. menuKeys mirror the sidebar's.
    tabs: [
      { key: 'storeitems', label: 'Store Items',        menuKey: 'StockDashboard',          path: '/admin/StoreMaster' },
      { key: 'stores',     label: 'Stores (Main/Sub)',  menuKey: 'StockDashboard',          path: '/admin/StoreLocations' },
      { key: 'stock',      label: 'Stock',              menuKey: 'StockDashboard',          path: '/admin/StockDashboard' },
      { key: 'transfer',   label: 'Stock Transfer',     menuKey: 'StockDashboard',          path: '/admin/StockTransfer' },
      { key: 'history',    label: 'Stock History',      menuKey: 'StockTransactionHistory', path: '/admin/StockTransactionHistory' },
      { key: 'openclose',  label: 'Opening / Closing',  menuKey: 'StockDashboard',          path: '/admin/StockOpeningClosingReport' },
      { key: 'kitindent',  label: 'Kitchen Indent',     menuKey: 'StorePurchase',           path: '/admin/IndentEntry' },
      { key: 'indentlist', label: 'Indent Orders List', menuKey: 'StorePurchase',           path: '/admin/IndentList' },
      { key: 'purchase',   label: 'Purchase',           menuKey: 'PurchaseEntry',           path: '/admin/StorePurchase' },
      { key: 'supplier',   label: 'Suppliers',          menuKey: 'SupplierMaster',          path: '/admin/SupplierMaster' },
      { key: 'recipe',     label: 'Recipes',            menuKey: 'RecipeMaster',            path: '/admin/RecipeMaster' },
      { key: 'expiry',     label: 'Expiry Report',      menuKey: 'StockDashboard',          path: '/admin/ExpiryReport' },
    ],
  },

  {
    key: 'housekeeping',
    label: 'Housekeeping',
    icon: 'CleaningServices',
    tabs: [
      { key: 'dashboard', label: 'Overview', menuKey: 'HousekeepingDashboard', path: '/admin/Housekeeping/Dashboard' },
      { key: 'tasks',     label: 'Tasks',    menuKey: 'HousekeepingTasks',     path: '/admin/Housekeeping/Tasks' },
      { key: 'staff',     label: 'Staff',    menuKey: 'HousekeepingStaff',     path: '/admin/Housekeeping/Staff' },
    ],
  },

  {
    key: 'guestservices',
    label: 'Guest Services',
    icon: 'RoomService',
    subtitle: 'Spa, laundry and mini-bar.',
    tabs: [
      { key: 'spaBookings', label: 'Spa Bookings', menuKey: 'SpaBookings',        path: '/admin/Spa/Bookings' },
      { key: 'spaServices', label: 'Spa Services', menuKey: 'SpaServices',        path: '/admin/Spa/Services' },
      { key: 'therapists',  label: 'Therapists',   menuKey: 'SpaTherapists',      path: '/admin/Spa/Therapists' },
      { key: 'laundry',     label: 'Laundry',      menuKey: 'LaundryOrders',      path: '/admin/Laundry/Orders' },
      { key: 'laundryItems',label: 'Laundry Items',menuKey: 'LaundryItems',       path: '/admin/Laundry/Items' },
      { key: 'minibar',     label: 'Mini Bar',     menuKey: 'MiniBarConsumption', path: '/admin/MiniBar/Consumption' },
      { key: 'minibarItems',label: 'Mini Bar Items', menuKey: 'MiniBarItems',     path: '/admin/MiniBar/Items' },
    ],
  },

  {
    key: 'hr',
    label: 'HR & Payroll',
    icon: 'Groups',
    tabs: [
      { key: 'dashboard',  label: 'Overview',   menuKey: 'HrDashboard',       path: '/admin/Hr/Dashboard' },
      { key: 'employees',  label: 'Employees',  menuKey: 'HrEmployees',       path: '/admin/Hr/Employees' },
      { key: 'attendance', label: 'Attendance', menuKey: 'HrAttendance',      path: '/admin/Hr/Attendance' },
      { key: 'leave',      label: 'Leave',      menuKey: 'HrLeaveRequests',   path: '/admin/Hr/LeaveRequests' },
      { key: 'shifts',     label: 'Shifts',     menuKey: 'HrShifts',          path: '/admin/Hr/Shifts' },
      { key: 'payroll',    label: 'Payroll',    menuKey: 'HrPayroll',         path: '/admin/Hr/Payroll' },
      { key: 'salary',     label: 'Salary',     menuKey: 'HrSalaryPayments',  path: '/admin/Hr/SalaryPayments' },
      { key: 'loans',      label: 'Loans & Advances', menuKey: 'HrLoans',     path: '/admin/Hr/Loans' },
      { key: 'reports',    label: 'HR Reports', menuKey: 'HrReports',         path: '/admin/Hr/Reports' },
    ],
  },

  {
    key: 'finance',
    label: 'Finance',
    icon: 'Payments',
    tabs: [
      { key: 'payments',  label: 'Payments',    menuKey: 'Paymentreport',        path: '/admin/Paymentreport' },
      { key: 'pettycash', label: 'Petty Cash',  menuKey: 'PettyCashManagement',  path: '/admin/pettycashmanagement' },
      { key: 'wallet',    label: 'Guest Wallet', menuKey: 'Customerwallet',      path: '/admin/Customerwallet' },
      { key: 'gst',       label: 'GST',         menuKey: 'GstReports',           path: '/admin/GstReports' },
      { key: 'subscription', label: 'Subscription', menuKey: 'SubscriptionPaymentReport', path: '/admin/SubscriptionPaymentReport' },
    ],
  },

  {
    key: 'reports',
    label: 'Reports',
    icon: 'Assessment',
    subtitle: 'All analytics in one place.',
    tabs: [
      { key: 'booking',   label: 'Booking',    menuKey: 'Bookingfilter',         path: '/admin/Bookingfilter' },
      { key: 'invoice',   label: 'Invoice',    menuKey: 'InvoiceReport',         path: '/admin/InvoiceReport' },
      { key: 'guest',     label: 'Guest',      menuKey: 'Guestreport',           path: '/admin/Guestreport' },
      { key: 'package',   label: 'Package',    menuKey: 'packagereport',         path: '/admin/packagereport' },
      { key: 'addon',     label: 'Add-On',     menuKey: 'AddonProfitReport',     path: '/admin/AddonProfitReport' },
      { key: 'agent',     label: 'Agent',      menuKey: 'AgentReport',           path: '/admin/AgentReport' },
      { key: 'commission',label: 'Commission', menuKey: 'Agentcommissionreport', path: '/admin/Agentcommissionreport' },
      { key: 'hold',      label: 'Hold / Maint.', menuKey: 'Holdreport',         path: '/admin/Holdreport' },
      { key: 'nightaudit',label: 'Night Audit', menuKey: 'NightAuditReport',     path: '/admin/NightAuditReport' },
      { key: 'item',      label: 'Item',       menuKey: 'ItemReport',            path: '/admin/ItemReport' },
      { key: 'expiry',    label: 'Expiry Report', menuKey: 'ExpiryReport',       path: '/admin/ExpiryReport' },
      { key: 'restGuest', label: 'Restaurant Guests', menuKey: 'RestaurantGuestReport', path: '/admin/RestaurantGuestReport' },
      { key: 'custDue',   label: 'Customer Dues', menuKey: 'CustomerDueReport',      path: '/admin/CustomerDueReport' },
      { key: 'activity',  label: 'Activity Log', menuKey: 'ActivityLog',         path: '/admin/ActivityLog' },
    ],
  },

  {
    key: 'communication',
    label: 'Communication',
    icon: 'Forum',
    tabs: [
      { key: 'tickets', label: 'Support Tickets', menuKey: 'SupportTickets', path: '/admin/SupportTickets' },
      { key: 'admin',   label: 'Support Admin',   menuKey: 'SupportAdmin',   path: '/admin/SupportAdmin' },
    ],
  },

  {
    key: 'settings',
    label: 'Settings',
    icon: 'Settings',
    subtitle: 'Property, menu and user configuration.',
    tabs: [
      // Property
      { key: 'hotels',    label: 'Hotels',      menuKey: 'hotels',        path: '/admin/hotels' },
      { key: 'rooms',     label: 'Rooms',       menuKey: 'rooms/add',     path: '/admin/rooms/add' },
      { key: 'roomtype',  label: 'Room Types',  menuKey: 'roomtype',      path: '/admin/roomtype' },
      { key: 'packages',  label: 'Packages',    menuKey: 'managepackage', path: '/admin/managepackage' },
      { key: 'addons',    label: 'Add-Ons',     menuKey: 'Addonm',        path: '/admin/Addonm' },
      // Restaurant masters (kept OUT of the Restaurant module - config, not ops)
      { key: 'restMasters', label: 'Restaurant Masters', menuKey: 'restaurant', path: '/admin/restaurant' },
      { key: 'categories',  label: 'Categories', menuKey: 'Category',     path: '/admin/Category' },
      { key: 'items',       label: 'Items',      menuKey: 'AddItem',      path: '/admin/AddItem' },
      { key: 'areas',       label: 'Areas',      menuKey: 'Createarea',   path: '/admin/Createarea' },
      { key: 'pricing',     label: 'Area Pricing', menuKey: 'AreaPricing', path: '/admin/AreaPricing' },
      { key: 'variants',    label: 'Variants',   menuKey: 'VariantManagement', path: '/admin/VariantManagement' },
      { key: 'stores',      label: 'Stores',     menuKey: 'StoreMaster',  path: '/admin/StoreMaster' },
      { key: 'storeMap',    label: 'Store Mapping', menuKey: 'ItemStoreMapping', path: '/admin/ItemStoreMapping' },
      // People
      { key: 'users',      label: 'Users',       menuKey: 'CreateUser',    path: '/admin/CreateUser' },
      { key: 'perms',      label: 'Permissions', menuKey: 'UserPermission', path: '/admin/UserPermission' },
      { key: 'agents',     label: 'Agents',      menuKey: 'Agent',         path: '/admin/Agent' },
      { key: 'coupons',    label: 'Coupons',     menuKey: 'Couponmaster',  path: '/admin/Couponmaster' },
    ],
  },
];

/** Flat lookup: route path -> { module, tab }. Powers deep links + Ctrl+K. */
export const ROUTE_INDEX = (() => {
  const idx = {};
  for (const m of MODULES) {
    for (const t of [...(m.tabs || []), ...(m.actions || [])]) {
      idx[t.path.toLowerCase()] = { moduleKey: m.key, tabKey: t.key, menuKey: t.menuKey, label: t.label };
    }
  }
  return idx;
})();

/** Every menuKey used by a module (for sidebar visibility). */
export const moduleMenuKeys = (m) =>
  [...(m.tabs || []), ...(m.actions || [])].map((t) => t.menuKey);

/** Filter modules+tabs by the SAME rule the sidebar uses today. */
export function visibleModules(hasKey, planAllowed) {
  const ok = (k) => hasKey(k) && planAllowed(k);
  return MODULES.map((m) => ({
    ...m,
    tabs: (m.tabs || []).filter((t) => ok(t.menuKey)),
    actions: (m.actions || []).filter((a) => ok(a.menuKey)),
  })).filter((m) => m.tabs.length > 0 || m.actions.length > 0);
}

export default MODULES;